import { IconCopy, IconExternalLink } from '@tabler/icons-react';
import { Badge } from '@/admin/components/ui/badge';
import { Button } from '@/admin/components/ui/button';
import { Card, CardContent } from '@/admin/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/admin/components/ui/table';
import type { AdminChat, AdminChatMetricSummary } from '../../types';

function formatDateTime(value?: string | null) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function shortId(value?: string | null) {
  if (!value) return '-';
  if (value.length <= 18) return value;
  return `${value.slice(0, 8)}...${value.slice(-6)}`;
}

function formatCount(value: number) {
  return new Intl.NumberFormat('en-US').format(value);
}

function formatPercent(value: number) {
  if (!Number.isFinite(value)) return '-';
  return `${Math.round(value * 100)}%`;
}

function metricChipLabel(label: string, summary?: AdminChatMetricSummary) {
  if (
    !summary ||
    summary.average_ms === null ||
    summary.average_ms === undefined
  ) {
    return `${label} -`;
  }
  const p95 =
    summary.p95_ms === null || summary.p95_ms === undefined
      ? '-'
      : `${Math.round(summary.p95_ms)} p95`;
  return `${label} ${Math.round(summary.average_ms)} avg / ${p95}`;
}

function qualityVariant(
  value?: string
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (value) {
    case 'exact':
    case 'backfilled':
      return 'secondary';
    case 'ambiguous':
    case 'source_retained_out':
      return 'destructive';
    default:
      return 'outline';
  }
}

function copyText(value?: string | null) {
  if (!value) return;
  void navigator.clipboard?.writeText(value);
}

function IdCopyButton({
  value,
  label,
}: {
  value?: string | null;
  label: string;
}) {
  if (!value) {
    return <span className="text-muted-foreground">-</span>;
  }
  return (
    <button
      type="button"
      className="inline-flex max-w-full items-center gap-1 rounded px-1 py-0.5 text-left font-mono text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
      onClick={() => copyText(value)}
      title={`Copy ${label}`}
    >
      <span className="truncate">{shortId(value)}</span>
      <IconCopy className="size-3 shrink-0" />
    </button>
  );
}

export function ChatTable({
  chats,
  loading,
  error,
  nextCursor,
  canGoPrevious,
  onOpenDetail,
  onPreviousPage,
  onNextPage,
}: {
  chats: AdminChat[];
  loading: boolean;
  error: string | null;
  nextCursor: string;
  canGoPrevious: boolean;
  onOpenDetail: (chat: AdminChat) => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        {error ? (
          <div className="mb-3 rounded-md border border-destructive/30 px-3 py-2 text-sm text-destructive">
            {error.toLowerCase().includes('analytics unavailable')
              ? 'Chat analytics are unavailable.'
              : error}
          </div>
        ) : null}
        <div className="overflow-hidden rounded-md border">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Session</TableHead>
                  <TableHead>Conversation</TableHead>
                  <TableHead>Agent / Group</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last activity</TableHead>
                  <TableHead>Counts</TableHead>
                  <TableHead>Tokens</TableHead>
                  <TableHead>Latency</TableHead>
                  <TableHead>Quality</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={13}
                      className="h-32 text-center text-muted-foreground"
                    >
                      Loading chats...
                    </TableCell>
                  </TableRow>
                ) : !error && chats.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={13}
                      className="h-32 text-center text-muted-foreground"
                    >
                      No chats match the current filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  chats.map(chat => (
                    <TableRow
                      key={`${chat.tenant_id}:${chat.agent_id}:${chat.session_id}`}
                    >
                      <TableCell className="min-w-[220px] whitespace-normal">
                        <div className="font-medium">{chat.title || '-'}</div>
                        <div className="text-xs text-muted-foreground">
                          {chat.purpose || '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <IdCopyButton value={chat.user_id} label="user id" />
                      </TableCell>
                      <TableCell>
                        <IdCopyButton
                          value={chat.session_id}
                          label="session id"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <IdCopyButton
                            value={chat.conversation_id}
                            label="conversation id"
                          />
                          <Badge
                            variant={qualityVariant(
                              chat.conversation_id_quality
                            )}
                            className="w-fit"
                          >
                            {chat.conversation_id_quality}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="min-w-[160px]">
                        <div className="flex flex-col gap-1">
                          <IdCopyButton
                            value={chat.agent_id}
                            label="agent id"
                          />
                          <IdCopyButton
                            value={chat.agent_group_id}
                            label="agent group id"
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge
                            variant={qualityVariant(
                              chat.status === 'error' ? 'ambiguous' : 'exact'
                            )}
                          >
                            {chat.status || '-'}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {chat.hidden
                              ? 'hidden'
                              : chat.activity_status || '-'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>{formatDateTime(chat.created_at)}</TableCell>
                      <TableCell>
                        {formatDateTime(chat.last_activity_at)}
                      </TableCell>
                      <TableCell className="min-w-[150px] text-xs">
                        <div>{formatCount(chat.message_count)} messages</div>
                        <div>
                          {formatCount(chat.tool_call_count)} tools /{' '}
                          {formatPercent(chat.tool_success_rate)}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div>{formatCount(chat.total_tokens)}</div>
                        <div className="text-muted-foreground">
                          cache{' '}
                          {formatCount(
                            chat.cache_read_input_tokens +
                              chat.cache_write_input_tokens
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="min-w-[230px] text-xs">
                        <div className="flex flex-wrap gap-1">
                          {[
                            metricChipLabel(
                              'msg lat',
                              chat.message_latency_summary
                            ),
                            metricChipLabel(
                              'msg dur',
                              chat.message_duration_summary
                            ),
                            metricChipLabel('tool', chat.tool_duration_summary),
                            metricChipLabel('TTFT', chat.turn_ttft_summary),
                            metricChipLabel('turn', chat.turn_total_summary),
                          ].map(label => (
                            <Badge
                              key={label}
                              variant="outline"
                              className="font-normal"
                            >
                              {label}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="min-w-[150px]">
                        <div className="flex flex-wrap gap-1">
                          <Badge
                            variant={qualityVariant(
                              chat.user_attribution_quality
                            )}
                          >
                            user {chat.user_attribution_quality}
                          </Badge>
                          <Badge variant={qualityVariant(chat.content_quality)}>
                            content {chat.content_quality}
                          </Badge>
                          <Badge variant={qualityVariant(chat.token_quality)}>
                            token {chat.token_quality}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onOpenDetail(chat)}
                          aria-label={`Open ${chat.session_id}`}
                        >
                          <IconExternalLink className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {chats.length} chats
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onPreviousPage}
              disabled={loading || !canGoPrevious}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onNextPage}
              disabled={loading || !nextCursor}
            >
              Next
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
